/**
 * astroHomeNormalize — the PURE, firebase-free half of the group/contact
 * Astro-Vastu seam (`astroHome.ts`). Kept in its own module so the defensive
 * coercion logic is unit-testable via the Node native runner (the seam file
 * itself imports firebase and cannot be loaded in a bare test process).
 *
 * It owns the TOLERANCE CONTRACT for the `resolveAstroGroup` callable: Forge's
 * exact wire shape is still being finalised on an isolated branch, so every
 * field is optional, every key is accepted in both camelCase and snake_case,
 * and anything unexpected coerces to a safe default. A partial payload can
 * NEVER trip a runtime error — the worst case is a member row with `—` cells.
 *
 * Types are REUSED from `astroVastu.ts` (`AstroBoxRow`, `AstroMember`) via a
 * type-only import (erased at build; keeps this module firebase-free). The group
 * summary member `Pick`s the fields it shares with `AstroMember` rather than
 * redeclaring them, and adds only the four room-direction summary cells + the
 * per-member chart ref that the GROUP summary contract names but a single-project
 * `AstroMember` does not carry.
 */
import type { AstroBoxRow, AstroMember } from './astroVastu';

export type { AstroBoxRow } from './astroVastu';

/**
 * One member row of the group/contact Astro-Vastu result. Reuses the shared
 * `AstroMember` field definitions for the columns they have in common
 * (identity, role, ascendant, chart status, the 12-row box matrix) and adds the
 * four room-direction summary cells the consultation-hub table renders
 * (Bedroom · Entrance · Office · Study), plus an optional per-member chart ref.
 */
export interface AstroGroupMember
  extends Pick<
    AstroMember,
    'personId' | 'name' | 'relationToClient' | 'isBreadwinner' | 'ascendantSign' | 'chartStatus' | 'boxMatrix'
  > {
  /** Recommended bedroom direction/zone; null while the engine has none. */
  bedroomDir: string | null;
  /** Recommended entrance direction/zone; null while the engine has none. */
  entranceDir: string | null;
  /** Recommended office direction/zone; null while the engine has none. */
  officeDir: string | null;
  /** Recommended study direction/zone; null while the engine has none. */
  studyDir: string | null;
  /** Per-member individual chart PDF URL, when the engine caches one. */
  chartRef: string | null;
}

/** The resolved group/contact Astro-Vastu view. */
export interface AstroGroupResolved {
  /** Always 'resolved' — a null return from the seam means "unavailable". */
  status: 'resolved';
  members: AstroGroupMember[];
  /** ISO timestamp the engine computed this view, when supplied. */
  generatedAt: string | null;
}

/** The raw wire response — every field optional so a partial payload is safe. */
export interface RawGroupResponse {
  members?: unknown;
  generatedAt?: unknown;
  generated_at?: unknown;
}

/* ── defensive coercion helpers (mirrors astroVastu.ts) ── */

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function bool(v: unknown): boolean {
  return v === true;
}
function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
/** First non-empty string among several candidate keys (any casing). */
function pickStr(v: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const s = strOrNull(v[k]);
    if (s) return s;
  }
  return null;
}

/** One Comparative Box Matrix row — mirrors astroVastu's `normalizeBoxRow`. */
function normalizeBoxRow(v: unknown): AstroBoxRow | null {
  if (!isObj(v)) return null;
  const box = num(v.box ?? v.box_no ?? v.boxNo, 0);
  // Box must be 1..12 — a 0/absent box is unusable.
  if (!box) return null;
  return {
    box,
    lifeContext: str(v.lifeContext ?? v.life_context),
    sign: str(v.sign ?? v.rashi),
    direction: str(v.direction ?? v.dir),
    lord: str(v.lord) || undefined,
    directions: arr(v.directions)
      .map((x) => str(x))
      .filter((s) => s.length > 0),
  };
}

/** One group/contact member — tolerant to camelCase AND snake_case wire keys. */
export function normalizeGroupMember(v: unknown): AstroGroupMember | null {
  if (!isObj(v)) return null;
  const personId = str(v.personId ?? v.person_id ?? v.contactId ?? v.contact_id);
  const name = str(v.name ?? v.displayName ?? v.display_name);
  // A row with neither an id nor a name is unusable — drop it rather than render
  // a blank card.
  if (!personId && !name) return null;
  const chartStatus = v.chartStatus === 'resolved' || v.chart_status === 'resolved' ? 'resolved' : 'pending';
  return {
    personId: personId || name,
    name: name || personId,
    relationToClient: str(v.relationToClient ?? v.relation_to_client ?? v.role ?? v.relation),
    isBreadwinner: bool(v.isBreadwinner ?? v.is_breadwinner),
    ascendantSign: pickStr(v, 'ascendantSign', 'ascendant_sign', 'ascendant'),
    chartStatus,
    bedroomDir: pickStr(v, 'bedroomDir', 'bedroom_direction', 'bedroomDirection', 'bedroom'),
    entranceDir: pickStr(v, 'entranceDir', 'entrance_direction', 'entranceDirection', 'entrance'),
    officeDir: pickStr(v, 'officeDir', 'office_direction', 'officeDirection', 'office'),
    studyDir: pickStr(v, 'studyDir', 'study_direction', 'studyDirection', 'study'),
    chartRef: pickStr(v, 'chartRef', 'chart_ref', 'pdfUrl', 'pdf_url', 'chartUrl', 'chart_url', 'url'),
    boxMatrix: arr(v.boxMatrix ?? v.box_matrix ?? v.boxes)
      .map(normalizeBoxRow)
      .filter((b): b is AstroBoxRow => b !== null),
  };
}

/**
 * Coerce any wire payload into a total, safe `AstroGroupResolved`. Always
 * returns a resolved object (possibly with zero members) — an empty member list
 * is the caller's cue to show the honest pending state, never a crash.
 */
export function normalizeGroupResponse(raw: RawGroupResponse | null | undefined): AstroGroupResolved {
  const r = raw ?? {};
  return {
    status: 'resolved',
    members: arr(r.members)
      .map(normalizeGroupMember)
      .filter((m): m is AstroGroupMember => m !== null),
    generatedAt: strOrNull(r.generatedAt ?? r.generated_at),
  };
}

/** Extract a PDF URL from `renderAstroPdf`'s payload, any key/casing. `null` = none. */
export function extractPdfUrl(data: unknown): string | null {
  if (!isObj(data)) return null;
  return pickStr(data, 'url', 'pdfUrl', 'pdf_url', 'chartUrl', 'chart_url');
}
